<template>
  <div v-if="visible" class="gx-import-dialog" @click.self="handleClose">
    <section class="gx-import-dialog__box" role="dialog" aria-modal="true" aria-label="新增商品">
      <header class="gx-import-dialog__head">
        <div class="gx-import-dialog__title-wrap">
          <span class="gx-import-dialog__title">新增商品</span>
          <template v-if="view !== 'menu'">
            <span class="gx-import-dialog__divider">/</span>
            <span class="gx-import-dialog__mode" :style="modeStyle(currentMode)">
              <span class="gx-import-dialog__mode-icon">{{ currentMode.icon }}</span>
              {{ currentMode.name }}
            </span>
          </template>
        </div>
        <div class="gx-import-dialog__head-actions">
          <button v-if="view !== 'menu'" type="button" class="gx-import-dialog__back" @click="view = 'menu'">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M6 2 2 5l4 3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" /></svg>
            返回
          </button>
          <button type="button" class="gx-import-dialog__close" aria-label="关闭" :disabled="uploading"
                  @click="handleClose">
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true"><path d="m1 1 9 9m0-9-9 9" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" /></svg>
          </button>
        </div>
      </header>

      <main v-if="view === 'menu'" class="gx-import-dialog__body gx-import-dialog__menu">
        <button
            v-for="(item, index) in importModes"
            :key="item.key"
            type="button"
            class="gx-import-dialog__mode-card"
            :class="[{ 'gx-import-dialog__mode-card--disabled': !item.enabled }, `gx-import-dialog__mode-card--${item.key}`]"
            :style="modeStyle(item)"
            @click="selectMode(item)"
        >
          <span class="gx-import-dialog__number">{{ String(index + 1).padStart(2, '0') }}</span>
          <span class="gx-import-dialog__icon">{{ item.icon }}</span>
          <span class="gx-import-dialog__mode-copy">
            <span class="gx-import-dialog__mode-name">{{ item.name }} <small v-if="item.badge">{{ item.badge }}</small></span>
            <span class="gx-import-dialog__mode-desc">{{ item.desc }}</span>
          </span>
          <span class="gx-import-dialog__arrow">›</span>
        </button>
      </main>

      <main v-else-if="view === 'link'" class="gx-import-dialog__body">
        <div class="gx-import-dialog__notice gx-import-dialog__notice--amber">
          <i aria-hidden="true"></i>
          <span>仅支持 1688 商品链接，每行一个，单次最多 10,000 条。</span>
        </div>
        <label class="gx-import-dialog__label" for="cloud-import-links">商品链接（每行一个）</label>
        <textarea id="cloud-import-links" v-model="linkText" class="gx-import-dialog__textarea" rows="5"
                  placeholder="https://detail.1688.com/offer/xxxxxxxx.html"/>
        <div class="gx-import-dialog__count">已识别 {{ parsedLinks.validIds.length }} 条 1688 商品链接<span
            v-if="parsedLinks.invalidCount">，{{ parsedLinks.invalidCount }} 条格式无效</span></div>
        <div class="gx-import-dialog__form-grid">
          <div>
            <span class="gx-import-dialog__label">来源平台</span>
            <div class="gx-import-dialog__source-card">1688</div>
          </div>
          <label class="gx-import-dialog__group">
            <span class="gx-import-dialog__label">加入子分组</span>
            <select v-model="linkChildGroupId" class="gx-import-dialog__select">
              <option value="">不加入分组</option>
              <option v-for="group in childGroups" :key="group.id" :value="group.id">{{ group.displayName }}</option>
            </select>
          </label>
        </div>
      </main>

      <main v-else class="gx-import-dialog__body">
        <div class="gx-import-dialog__notice gx-import-dialog__notice--blue">
          <i aria-hidden="true"></i>
          <span>仅支持合作商品包；仅支持 ZIP，单文件不超过 100 MB。</span>
        </div>
        <span class="gx-import-dialog__label">数据包类型</span>
        <div class="gx-import-dialog__package-types gx-import-dialog__package-types--single">
          <span class="gx-import-dialog__package-type gx-import-dialog__package-type--on"><b>🤝</b>合作商品包</span>
        </div>
        <input ref="packageInput" class="gx-import-dialog__file-input" type="file"
               accept=".zip,application/zip,application/x-zip-compressed" @change="handleFileChange"/>
        <div class="gx-import-dialog__drop-zone"
             :class="{ 'gx-import-dialog__drop-zone--busy': uploading, 'gx-import-dialog__drop-zone--ready': packageUpload.url }"
             @dragover.prevent @drop.prevent="handleDrop" @click="chooseFile">
          <template v-if="uploading">
            <strong>正在安全校验并上传… {{ packageUpload.progress }}%</strong>
            <span class="gx-import-dialog__progress"><i :style="{ width: `${packageUpload.progress}%` }"></i></span>
          </template>
          <template v-else-if="packageUpload.url">
            <strong>✓ {{ packageUpload.fileName }}</strong>
            <small>已安全校验并上传至 OSS</small>
          </template>
          <template v-else>
            <span class="gx-import-dialog__folder">📁</span>
            <strong>点击上传或将 ZIP 文件拖到此处</strong>
            <small>仅支持 .zip 格式，文件大小不超过 100 MB</small>
          </template>
        </div>
        <div v-if="packageUpload.error" class="gx-import-dialog__error">{{ packageUpload.error }}</div>
        <label class="gx-import-dialog__group">
          <span class="gx-import-dialog__label">加入子分组</span>
          <select v-model="packageChildGroupId" class="gx-import-dialog__select">
            <option value="">不加入分组</option>
            <option v-for="group in childGroups" :key="group.id" :value="group.id">{{ group.displayName }}</option>
          </select>
        </label>
      </main>

      <footer class="gx-import-dialog__foot">
        <span>{{
            view === 'menu' ? '选择一种导入方式继续' : `当前方式：${view === 'link' ? '链接导入' : '数据包导入'}`
          }}</span>
        <div v-if="view !== 'menu'" class="gx-import-dialog__foot-actions">
          <button type="button" class="gx-import-dialog__button" :disabled="uploading" @click="view = 'menu'">返回
          </button>
          <button type="button" class="gx-import-dialog__button gx-import-dialog__button--primary" :disabled="uploading"
                  @click="confirmCurrent">确定导入
          </button>
        </div>
        <button v-else type="button" class="gx-import-dialog__button" @click="handleClose">取消</button>
      </footer>
    </section>
  </div>
</template>

<script>
import JSZip from 'jszip'
import {uploadDirectOss} from '@/assets/js/utils/ossDirectUpload'

const MAX_FILE_SIZE = 100 * 1024 * 1024
const MAX_ZIP_ENTRIES = 10000
const MAX_UNCOMPRESSED_SIZE = 200 * 1024 * 1024
const MAX_ENTRY_UNCOMPRESSED_SIZE = 5 * 1024 * 1024
const MAX_COMPRESSION_RATIO = 100

export default {
  name: 'ProductImportDialog',
  props: {
    visible: {type: Boolean, default: false},
    groups: {type: Array, default: () => []}
  },
  data() {
    return {
      view: 'menu',
      linkText: '',
      linkChildGroupId: '',
      packageChildGroupId: '',
      uploading: false,
      packageUpload: {fileName: '', url: '', progress: 0, error: ''},
      importModes: [
        {
          key: 'publish',
          icon: '✍️',
          name: '发布商品',
          badge: '手动创建',
          desc: '手动填写商品标题、主图、SKU 等信息',
          accent: '#52C41A',
          background: 'rgba(82, 196, 26, .07)',
          border: 'rgba(82, 196, 26, .25)',
          enabled: false
        },
        {
          key: 'link',
          icon: '🔗',
          name: '链接导入',
          badge: '仅支持 1688',
          desc: '粘贴 1688 商品链接，批量解析自动入库',
          accent: '#D4A946',
          background: 'rgba(212, 169, 70, .07)',
          border: 'rgba(212, 169, 70, .28)',
          enabled: true
        },
        {
          key: 'package',
          icon: '📁',
          name: '数据包导入',
          badge: '合作商品包',
          desc: '上传合作商品数据包并自动识别导入',
          accent: '#3FB6E0',
          background: 'rgba(63, 182, 224, .07)',
          border: 'rgba(63, 182, 224, .28)',
          enabled: true
        },
        {
          key: 'template',
          icon: '📋',
          name: '数据包格式导入',
          badge: '参考模板格式',
          desc: '下载标准模板，按格式填写后上传',
          accent: '#8B5CF6',
          background: 'rgba(139, 92, 246, .07)',
          border: 'rgba(139, 92, 246, .28)',
          enabled: false
        },
        {
          key: 'shop',
          icon: '🔄',
          name: '店铺同步商品导入',
          badge: '已授权店铺',
          desc: '从已授权店铺同步商品到云商品库',
          accent: '#3FB6E0',
          background: 'rgba(63, 182, 224, .07)',
          border: 'rgba(63, 182, 224, .28)',
          enabled: false
        }
      ],
    }
  },
  computed: {
    currentMode() {
      return this.importModes.find(item => item.key === this.view) || {}
    },
    childGroups() {
      return this.groups.reduce((items, parent) => {
        if (parent.is_shared) return items
        const children = Array.isArray(parent.child_groups) ? parent.child_groups : []
        children.forEach(child => {
          if (child.is_shared) return
          items.push({
            ...child,
            displayName: `${parent.name || '未命名分组'} / ${child.name || '未命名子分组'}`
          })
        })
        return items
      }, [])
    },
    validChildGroupIds() {
      return new Set(this.childGroups.map(group => String(group.id)))
    },
    parsedLinks() {
      const validIds = []
      const seen = new Set()
      let invalidCount = 0
      this.linkText.split(/[\r\n]+/).map(item => item.trim()).filter(Boolean).forEach(link => {
        const productId = this.get1688ProductId(link)
        if (!productId) {
          invalidCount += 1
          return
        }
        if (!seen.has(productId)) {
          seen.add(productId)
          validIds.push(productId)
        }
      })
      return {validIds, invalidCount}
    }
  },
  watch: {
    visible(value) {
      if (value) this.reset()
    }
  },
  methods: {
    modeStyle(item) {
      return {
        '--gx-accent': item.accent || '#D4A946',
        '--gx-mode-background': item.background || 'rgba(212, 169, 70, .07)',
        '--gx-mode-border': item.border || 'rgba(212, 169, 70, .28)'
      }
    },
    reset() {
      this.view = 'menu'
      this.linkText = ''
      this.linkChildGroupId = ''
      this.packageChildGroupId = ''
      this.uploading = false
      this.packageUpload = {fileName: '', url: '', progress: 0, error: ''}
      this.$nextTick(() => {
        if (this.$refs.packageInput) this.$refs.packageInput.value = ''
      })
    },
    handleClose() {
      if (!this.uploading) this.$emit('update:visible', false)
    },
    selectMode(item) {
      if (!item.enabled) {
        this.$message.info('功能开发中')
        return
      }
      this.view = item.key
    },
    get1688ProductId(link) {
      try {
        const url = new URL(link)
        if (!/(^|\.)1688\.com$/i.test(url.hostname)) return ''
        const match = url.pathname.match(/\/offer\/(\d+)\.html$/i)
        return match ? match[1] : ''
      } catch (e) {
        return ''
      }
    },
    ensureValidChildGroup(field) {
      const childGroupId = this[field]
      if (childGroupId === '' || childGroupId === null || childGroupId === undefined) return true
      if (this.validChildGroupIds.has(String(childGroupId))) return true
      this[field] = ''
      this.$message.warning('所选子分组不可用，请重新选择')
      return false
    },
    confirmCurrent() {
      if (this.view === 'link') {
        const {validIds, invalidCount} = this.parsedLinks
        if (!validIds.length) return this.$message.error('请输入有效的 1688 商品链接')
        if (invalidCount) return this.$message.error(`存在 ${invalidCount} 条非 1688 商品链接，请修正后重试`)
        if (validIds.length > 10000) return this.$message.error('单次最多导入 10,000 条 1688 商品链接')
        if (!this.ensureValidChildGroup('linkChildGroupId')) return
        this.$emit('import-1688', {productIds: validIds, childGroupId: this.linkChildGroupId})
        return
      }
      if (!this.packageUpload.url) return this.$message.error('请先完成数据包上传')
      if (!this.ensureValidChildGroup('packageChildGroupId')) return
      this.$emit('import-package', {
        ossUrl: this.packageUpload.url,
        fileName: this.packageUpload.fileName,
        childGroupId: this.packageChildGroupId
      })
    },
    chooseFile() {
      if (!this.uploading && this.$refs.packageInput) this.$refs.packageInput.click()
    },
    handleDrop(event) {
      if (!this.uploading) this.uploadPackage((event.dataTransfer.files || [])[0])
    },
    handleFileChange(event) {
      this.uploadPackage((event.target.files || [])[0])
    },
    async uploadPackage(file) {
      if (!file || this.uploading) return
      this.packageUpload = {fileName: '', url: '', progress: 0, error: ''}
      try {
        await this.validateZipPackage(file)
        this.uploading = true
        this.packageUpload.fileName = file.name
        const result = await uploadDirectOss(file, {
          purpose: 'cloud_import_package',
          timeout: 180000,
          onProgress: ({percent}) => {
            this.packageUpload.progress = percent
          }
        })
        this.packageUpload.url = result.url || result.address || ''
        if (!this.packageUpload.url) throw new Error('OSS 未返回文件链接')
        this.packageUpload.progress = 100
        this.$message.success('数据包已安全校验并上传至 OSS')
      } catch (error) {
        this.packageUpload.error = error && error.message ? error.message : '数据包上传失败，请重试'
      } finally {
        this.uploading = false
        if (this.$refs.packageInput) this.$refs.packageInput.value = ''
      }
    },
    async validateZipPackage(file) {
      const name = String(file.name || '')
      if (!/\.zip$/i.test(name)) throw new Error('仅支持 ZIP 数据包')
      if (!file.size) throw new Error('不允许上传空数据包')
      if (file.size > MAX_FILE_SIZE) throw new Error('数据包不能超过 100 MB')
      const signature = new Uint8Array(await this.readAsArrayBuffer(file.slice(0, 4)))
      const isZip = signature[0] === 0x50 && signature[1] === 0x4b && ((signature[2] === 0x03 && signature[3] === 0x04) || (signature[2] === 0x05 && signature[3] === 0x06) || (signature[2] === 0x07 && signature[3] === 0x08))
      if (!isZip) throw new Error('文件内容不是有效的 ZIP 数据包')

      let archive
      try {
        archive = await JSZip.loadAsync(file, {createFolders: false})
      } catch (error) {
        throw new Error('ZIP 数据包损坏、加密或无法读取')
      }
      const entries = Object.values(archive.files || {})
      if (!entries.length) throw new Error('不允许上传空 ZIP 数据包')
      if (entries.length > MAX_ZIP_ENTRIES) throw new Error(`ZIP 文件数量不能超过 ${MAX_ZIP_ENTRIES} 个`)

      let uncompressedSize = 0
      let fileCount = 0
      for (const entry of entries) {
        const unsafeName = String(entry.unsafeOriginalName || entry.name || '')
        if (!unsafeName || unsafeName.length > 255 || unsafeName.includes('\\') || unsafeName.startsWith('/') || unsafeName.split('/').includes('..')) {
          throw new Error('ZIP 包含不安全的文件路径')
        }
        if (entry.dir) continue
        fileCount += 1
        if (!/\.(txt|json)$/i.test(unsafeName)) throw new Error('ZIP 内仅允许包含 .txt 或 .json 文件')
        const data = entry._data || {}
        const entrySize = Number(data.uncompressedSize)
        const compressedSize = Number(data.compressedSize)
        if (!Number.isFinite(entrySize) || entrySize < 0 || entrySize > MAX_ENTRY_UNCOMPRESSED_SIZE) throw new Error('ZIP 包含异常大小的文件')
        if (compressedSize > 0 && entrySize / compressedSize > MAX_COMPRESSION_RATIO) throw new Error('ZIP 压缩比异常，可能存在解压风险')
        uncompressedSize += entrySize
        if (uncompressedSize > MAX_UNCOMPRESSED_SIZE) throw new Error('ZIP 解压后的总大小不能超过 200 MB')
      }
      if (!fileCount) throw new Error('ZIP 数据包中未找到可导入文件')
    },
    readAsArrayBuffer(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = event => resolve(event.target.result)
        reader.onerror = () => reject(new Error('读取数据包失败'))
        reader.readAsArrayBuffer(blob)
      })
    }
  }
}
</script>

<style scoped>
.gx-import-dialog {
  position: fixed;
  inset: 0;
  z-index: 760;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, .48);
  backdrop-filter: blur(6px);
}

.gx-import-dialog__box {
  width: min(640px, 100%);
  overflow: hidden;
  border: 1px solid #e1e5ee;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 20px 56px rgba(15, 23, 42, .22);
}

.gx-import-dialog__head, .gx-import-dialog__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  background: #f5f0e8;
}

.gx-import-dialog__head {
  border-bottom: 1px solid #e1e5ee;
}

.gx-import-dialog__title-wrap, .gx-import-dialog__head-actions, .gx-import-dialog__foot-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.gx-import-dialog__title {
  position: relative;
  padding-left: 10px;
  color: #1a1a2e;
  font-family: 'Noto Serif SC', serif;
  font-size: 15px;
  font-weight: 700;
}

.gx-import-dialog__title::before {
  position: absolute;
  top: 50%;
  left: 0;
  width: 3px;
  height: 15px;
  border-radius: 2px;
  background: #c8443c;
  content: '';
  transform: translateY(-50%);
}

.gx-import-dialog__divider {
  color: #c7cad3;
}

.gx-import-dialog__mode {
  color: #1f9dcc;
  font-size: 13px;
  font-weight: 600;
}

.gx-import-dialog__close, .gx-import-dialog__back, .gx-import-dialog__button {
  border: 1px solid #dfe3ed;
  border-radius: 6px;
  background: #fff;
  color: #4a4a6a;
  cursor: pointer;
}

.gx-import-dialog__close {
  width: 26px;
  height: 26px;
  font-size: 22px;
  line-height: 20px;
}

.gx-import-dialog__back {
  height: 26px;
  padding: 0 9px;
}

.gx-import-dialog__close:disabled {
  cursor: not-allowed;
  opacity: .55;
}

.gx-import-dialog__body {
  max-height: calc(90vh - 112px);
  overflow-y: auto;
  padding: 18px;
}

.gx-import-dialog__menu {
  display: grid;
  gap: 9px;
}

.gx-import-dialog__mode-card {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 66px;
  gap: 13px;
  padding: 10px 14px;
  border: 1px solid #dce1ec;
  border-radius: 10px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: border-color .16s, box-shadow .16s;
}

.gx-import-dialog__mode-card:not(.gx-import-dialog__mode-card--disabled):hover {
  border-color: #7dd3fc;
  box-shadow: 0 3px 12px rgba(56, 189, 248, .12);
}

.gx-import-dialog__mode-card--disabled {
  cursor: not-allowed;
  opacity: .48;
}

.gx-import-dialog__number {
  display: grid;
  width: 28px;
  height: 28px;
  place-items: center;
  border: 1px solid #cde2f7;
  border-radius: 7px;
  background: #f0f9ff;
  color: #269bd1;
  font-size: 11px;
}

.gx-import-dialog__mode-card--link .gx-import-dialog__number {
  border-color: #f1d6a0;
  background: #fffaf0;
  color: #d99a19;
}

.gx-import-dialog__mode-card--publish .gx-import-dialog__number {
  border-color: #c8efc6;
  background: #f4fff2;
  color: #55ad4d;
}

.gx-import-dialog__icon {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border: 1px solid #e1e5ee;
  border-radius: 8px;
  background: #f8fafc;
  font-size: 18px;
}

.gx-import-dialog__mode-copy {
  display: grid;
  gap: 5px;
  flex: 1;
}

.gx-import-dialog__mode-name {
  color: #25263d;
  font-size: 14px;
  font-weight: 700;
}

.gx-import-dialog__mode-name small {
  padding: 1px 7px;
  border-radius: 4px;
  background: #ecf9ff;
  color: rgb(82, 196, 26);
  font-size: 10px;
  font-weight: 500;
  border: 1px solid rgba(82, 196, 26, 0.25);
  font-family: "Noto Sans SC", sans-serif;
}

.gx-import-dialog__mode-desc {
  color: #9295a1;
  font-size: 11px;
}

.gx-import-dialog__arrow {
  color: #b9bdc8;
  font-size: 24px;
}

.gx-import-dialog__notice {
  margin-bottom: 16px;
  padding: 10px 12px;
  border: 1px solid;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
}

.gx-import-dialog__notice--amber {
  border-color: #f0d6a3;
  background: #fffaf0;
  color: #8b6b2a;
}

.gx-import-dialog__notice--blue {
  border-color: #b8e2f7;
  background: #f0faff;
  color: #537182;
}

.gx-import-dialog__label {
  display: block;
  margin-bottom: 7px;
  color: #5d6072;
  font-size: 12px;
}

.gx-import-dialog__textarea, .gx-import-dialog__select {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #dce1ec;
  border-radius: 7px;
  background: #fff;
  color: #303246;
  font: inherit;
  font-size: 12px;
  outline: none;
}

.gx-import-dialog__textarea {
  padding: 10px;
  resize: vertical;
}

.gx-import-dialog__textarea:focus, .gx-import-dialog__select:focus {
  border-color: #4bc1ee;
}

.gx-import-dialog__count {
  margin: 8px 0 14px;
  color: #8a8e9c;
  font-size: 11px;
}

.gx-import-dialog__group {
  display: block;
}

.gx-import-dialog__select {
  height: 34px;
  padding: 0 10px;
}

.gx-import-dialog__package-types {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 9px;
  margin-bottom: 14px;
}

.gx-import-dialog__package-type {
  display: grid;
  gap: 5px;
  min-height: 62px;
  place-content: center;
  border: 1px solid #dce1ec;
  border-radius: 8px;
  background: #f8f5ef;
  color: #4a4a62;
  cursor: pointer;
  font-size: 12px;
}

.gx-import-dialog__package-type--on {
  border-color: #3dbceb;
  background: #effaff;
  color: #20a5d6;
  font-weight: 700;
}

.gx-import-dialog__file-input {
  display: none;
}

.gx-import-dialog__drop-zone {
  display: grid;
  min-height: 130px;
  place-content: center;
  gap: 8px;
  padding: 14px;
  border: 2px dashed #dce2ef;
  border-radius: 9px;
  background: #fcfdff;
  color: #3b3d50;
  text-align: center;
  cursor: pointer;
}

.gx-import-dialog__drop-zone:hover {
  border-color: #8bd7f3;
}

.gx-import-dialog__drop-zone--busy {
  cursor: wait;
}

.gx-import-dialog__drop-zone--ready {
  border-color: #88d8a3;
  background: #f4fff6;
}

.gx-import-dialog__folder {
  font-size: 25px;
}

.gx-import-dialog__drop-zone small {
  color: #a2a5b0;
  font-size: 11px;
}

.gx-import-dialog__progress {
  display: block;
  width: 260px;
  max-width: 100%;
  height: 6px;
  overflow: hidden;
  border-radius: 9px;
  background: #e7ebf2;
}

.gx-import-dialog__progress i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #3dbceb;
  transition: width .15s;
}

.gx-import-dialog__error {
  margin: 8px 0 12px;
  color: #dd564e;
  font-size: 12px;
  line-height: 1.5;
}

.gx-import-dialog__foot {
  min-height: 52px;
  border-top: 1px solid #e1e5ee;
  color: #9295a1;
  font-size: 12px;
}

.gx-import-dialog__button {
  height: 32px;
  padding: 0 13px;
}

.gx-import-dialog__button--primary {
  border-color: #ff6e6e;
  background: #ff6e6e;
  color: #fff;
}

.gx-import-dialog__button:disabled {
  cursor: not-allowed;
  opacity: .55;
}

@media (max-width: 600px) {
  .gx-import-dialog {
    align-items: flex-start;
    padding: 12px;
  }

  .gx-import-dialog__box {
    margin-top: 8px;
  }

  .gx-import-dialog__body {
    padding: 14px;
  }

  .gx-import-dialog__mode-desc {
    display: none;
  }

  .gx-import-dialog__package-types {
    grid-template-columns: 1fr;
  }

  .gx-import-dialog__foot {
    align-items: flex-start;
    gap: 10px;
    flex-direction: column;
  }

  .gx-import-dialog__foot-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

/* 设计稿的颜色与尺寸仅作用于本导入弹窗，避免影响旧版 Element UI 弹窗。 */
.gx-import-dialog__box {
  display: flex;
  max-height: 90vh;
  border: 1.5px solid #e0e0ec;
  border-radius: 12px;
  box-shadow: 0 24px 72px rgba(0, 0, 0, .24);
  flex-direction: column;
}

.gx-import-dialog__head, .gx-import-dialog__foot {
  flex-shrink: 0;
  padding: 12px 18px;
  background: #f5f0e8;
}

.gx-import-dialog__head {
  border-bottom-color: #e0e0ec;
}

.gx-import-dialog__title-wrap, .gx-import-dialog__head-actions, .gx-import-dialog__foot-actions {
  gap: 8px;
}

.gx-import-dialog__title {
  color: #1a1a2e;
  font-family: 'Noto Sans SC', sans-serif;
  font-weight: 600;
}

.gx-import-dialog__mode {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border: 1px solid var(--gx-mode-border);
  border-radius: 5px;
  background: var(--gx-mode-background);
  color: var(--gx-accent);
  font-family: 'Noto Sans SC', sans-serif;
  font-size: 12px;
  font-weight: 600;
}

.gx-import-dialog__mode-icon {
  font-size: 12px;
}

.gx-import-dialog__close, .gx-import-dialog__back, .gx-import-dialog__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 1.5px solid #e0e0ec;
  border-radius: 5px;
  font-family: 'Noto Sans SC', sans-serif;
  font-size: 12px;
}

.gx-import-dialog__close {
  font-size: 0;
}

.gx-import-dialog__close svg {
  color: #666;
}

.gx-import-dialog__body {
  flex: 1;
  padding: 18px;
}

.gx-import-dialog__menu {
  gap: 8px;
}

.gx-import-dialog__mode-card {
  position: relative;
  min-height: 0;
  gap: 14px;
  overflow: hidden;
  padding: 13px 16px;
  border: 1.5px solid #e0e0ec;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .04);
}

.gx-import-dialog__mode-card::before {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 3px;
  border-radius: 10px 0 0 10px;
  background: var(--gx-accent);
  content: '';
  opacity: 0;
  transition: opacity .16s;
}

.gx-import-dialog__mode-card:not(.gx-import-dialog__mode-card--disabled):hover {
  border-color: var(--gx-accent);
  background: var(--gx-mode-background);
  box-shadow: 0 2px 7px rgba(0, 0, 0, .07);
}

.gx-import-dialog__mode-card:not(.gx-import-dialog__mode-card--disabled):hover::before {
  opacity: 1;
}

.gx-import-dialog__mode-card--disabled {
  opacity: .5;
}

.gx-import-dialog__mode-card .gx-import-dialog__number, .gx-import-dialog__icon {
  border: 1.5px solid var(--gx-mode-border);
  background: var(--gx-mode-background);
  color: var(--gx-accent);
}

.gx-import-dialog__number {
  border-radius: 7px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 700;
}

.gx-import-dialog__icon {
  width: 36px;
  height: 36px;
  border-radius: 9px;
}

.gx-import-dialog__mode-copy {
  gap: 2px;
}

.gx-import-dialog__mode-name {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #1a1a2e;
  font-family: 'Noto Sans SC', sans-serif;
  font-size: 13px;
  font-weight: 600;
}

.gx-import-dialog__mode-name small {
  padding: 1px 7px;
  border: 1px solid var(--gx-mode-border);
  border-radius: 4px;
  background: var(--gx-mode-background);
  color: var(--gx-accent);
  font-size: 10px;
  font-weight: 500;
}

.gx-import-dialog__mode-desc {
  color: #999;
  font-family: 'Noto Sans SC', sans-serif;
  line-height: 1.55;
}

.gx-import-dialog__arrow {
  color: #1a1a2e;
  font-size: 0;
  opacity: .3;
}

.gx-import-dialog__arrow::after {
  content: '›';
  font-size: 24px;
}

.gx-import-dialog__notice {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  padding: 9px 12px;
  border-radius: 8px;
  color: #666;
  font-family: 'Noto Sans SC', sans-serif;
}

.gx-import-dialog__notice i {
  width: 3px;
  height: 24px;
  border-radius: 2px;
  background: currentColor;
  flex-shrink: 0;
}

.gx-import-dialog__notice--amber {
  border-color: rgba(212, 169, 70, .35);
  background: rgba(212, 169, 70, .07);
  color: #d4a946;
}

.gx-import-dialog__notice--amber span, .gx-import-dialog__notice--blue span {
  color: #666;
}

.gx-import-dialog__notice--blue {
  border-color: rgba(63, 182, 224, .28);
  background: rgba(63, 182, 224, .07);
  color: #3fb6e0;
}

.gx-import-dialog__label {
  color: #666;
  font-family: 'Noto Sans SC', sans-serif;
  font-size: 11px;
  letter-spacing: .02em;
}

.gx-import-dialog__textarea, .gx-import-dialog__select {
  border: 1.5px solid #e0e0ec;
  border-radius: 5px;
  color: #1a1a2e;
  font-family: 'Noto Sans SC', sans-serif;
}

.gx-import-dialog__textarea {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  line-height: 1.75;
}

.gx-import-dialog__textarea:focus, .gx-import-dialog__select:focus {
  border-color: #d4a946;
}

.gx-import-dialog__count {
  margin: 3px 0 14px;
  color: #999;
  font-family: 'Noto Sans SC', sans-serif;
  font-size: 10px;
}

.gx-import-dialog__form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.gx-import-dialog__source-card {
  box-sizing: border-box;
  display: grid;
  height: 34px;
  place-items: center;
  border: 1.5px solid #d4a946;
  border-radius: 6px;
  background: rgba(212, 169, 70, .07);
  color: #d4a946;
  font-family: 'Noto Sans SC', sans-serif;
  font-size: 11px;
  font-weight: 700;
}

.gx-import-dialog__select {
  height: 34px;
}

.gx-import-dialog__package-types {
  margin-bottom: 14px;
}

.gx-import-dialog__package-types--single {
  display: block;
}

.gx-import-dialog__package-type.gx-import-dialog__package-type--on {
  display: flex;
  min-height: 0;
  height: 56px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 1.5px solid #3fb6e0;
  border-radius: 8px;
  background: rgba(63, 182, 224, .08);
  color: #3fb6e0;
  font-family: 'Noto Sans SC', sans-serif;
  font-size: 11px;
  font-weight: 600;
}

.gx-import-dialog__package-type b {
  font-size: 18px;
  font-weight: 400;
}

.gx-import-dialog__drop-zone {
  min-height: 0;
  padding: 24px 0;
  border-color: #e0e0ec;
  border-radius: 8px;
  background: #fafafa;
  font-family: 'Noto Sans SC', sans-serif;
}

.gx-import-dialog__drop-zone:hover {
  border-color: #3fb6e0;
  background: rgba(0, 0, 0, .015);
}

.gx-import-dialog__drop-zone--ready {
  border-color: #52c41a;
  background: rgba(82, 196, 26, .05);
}

.gx-import-dialog__drop-zone small {
  color: #999;
}

.gx-import-dialog__foot {
  min-height: 54px;
  border-top-color: #e0e0ec;
  color: #999;
  font-family: 'Noto Sans SC', sans-serif;
  font-size: 11px;
}

.gx-import-dialog__button {
  height: 32px;
  padding: 0 16px;
  color: #666;
  font-size: 13px;
}

.gx-import-dialog__button--primary {
  border: 0;
  background: linear-gradient(135deg, #ff4d4f, #ff7875);
  color: #fff;
  font-weight: 600;
}

@media (max-width: 600px) {
  .gx-import-dialog__form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
